// Component: TickerPrice.jsx
// Purpose: Animated per-digit price display used by the watchlist and the
//          live-trading ticker badge. Flip-animates each numeric digit on
//          value change with direction-based green/up or red/down flash that
//          fades over ~2s. Non-digit chars (₹, comma, decimal) are rendered
//          in matching 1em boxes so the whole price sits on one baseline.

import React, { useState, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

//--> V12B-07: resolve the INHERITED text colour to a real rgb() value.
//*   framer-motion cannot interpolate the CSS keyword `currentColor` — it is not a
//*   colour, it is "whatever this element inherits". Passing it as an animation
//*   endpoint made framer log
//*     "You are trying to animate color from currentColor to #00C292…"
//*   on essentially EVERY TICK. Cosmetic, but during the 2026-08-04 V12b live
//*   validation it flooded the console so badly that the `[WS-DELIVERY]` diagnostic
//*   lines could not be read out of the buffer at all and had to be captured with an
//*   in-page tap.
//!   This changes NOTHING when a caller passes an explicit `baseColor` (the common
//!   case) — it only replaces the FALLBACK. And the resolved value is precisely what
//!   `currentColor` already meant, so the digits end on the same colour they always
//!   did; they now FADE to it instead of framer refusing the interpolation.
//!   PROTECTED COMPONENT — colours, durations, easings, the spring and every layout
//!   class below are deliberately untouched.
const useInheritedColor = (ref, explicitColor) => {
  const [resolved, setResolved] = useState(null);

  useLayoutEffect(() => {
    //--> an explicit colour wins; no need to touch the DOM at all
    if (explicitColor || !ref.current) return undefined;

    const read = () => {
      if (!ref.current) return;
      const c = window.getComputedStyle(ref.current).color;
      //! only accept a real colour — never store the keyword back
      if (c && c !== 'currentColor') setResolved(c);
    };
    read();

    //! the inherited colour changes when the theme flips. Without this the digits
    //! would keep animating to the OLD theme's colour after a light/dark toggle —
    //! a visible regression, in a component that must not visibly change.
    //! Observing the root attribute is cheap: it fires on theme switches only, not
    //! on ticks.
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });
    return () => obs.disconnect();
  }, [ref, explicitColor]);

  return explicitColor || resolved;
};

//--> Match the motion.span color transition (duration 2.0s, circOut ease,
//*   delay 0.3s) so non-digit characters (₹, commas, decimal) fade in sync
//*   with the animated digits. Without this the bare <span> chars would snap
//*   instantly while digits drift — visibly inconsistent palette shift.
const NON_DIGIT_COLOR_TRANSITION = 'color 2s cubic-bezier(0,.55,.45,1) 0.3s';

// Single Digit Component with Animation
const TickerDigit = ({ char, direction, signalColor, baseColor }) => {
  if (isNaN(parseInt(char))) {
    //--> Non-digit chars sit inside the same 1em-tall box as digit containers
    //*   and flex-center their glyph, so ₹, commas, decimals land on the same
    //*   visual baseline as the animated digits. Uniform align-bottom on the
    //*   box + flex centering of the glyph mirrors the digit path.
    return (
      <span
        className="inline-flex items-center justify-center h-[1em] align-bottom"
        style={{ transition: NON_DIGIT_COLOR_TRANSITION }}
      >
        {char}
      </span>
    );
  }

  //! V12B-07: `baseColor` now arrives already resolved to a real colour (see
  //!  useInheritedColor). It is null ONLY on the very first frame before layout has
  //!  run. In that one frame we omit `color` from the animation entirely rather than
  //!  falling back to the `currentColor` keyword — framer cannot interpolate the
  //!  keyword, and feeding it one is exactly the defect being fixed. Omitting leaves
  //!  the digit on its inherited CSS colour, which is what it should be anyway.
  const colorAnim = baseColor ? { color: baseColor } : {};
  const colorFlash = signalColor ? { color: signalColor } : {};

  return (
    <span className="relative inline-block w-[0.6em] h-[1em] overflow-hidden align-bottom">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={char} // Key change triggers animation
          className="absolute inset-0 flex items-center justify-center font-bold"
          initial={{ y: direction === 'up' ? '100%' : '-100%', opacity: 0, ...colorFlash }}
          animate={{ y: '0%', opacity: 1, ...colorAnim }}
          exit={{ y: direction === 'up' ? '-100%' : '100%', opacity: 0, ...colorAnim }}
          transition={{
            y: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
            color: { duration: 2.0, ease: "circOut", delay: 0.3 } // Long fade out after motion
          }}
        >
          {char}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const TickerPrice = ({ value, currency = 'INR', className = '', baseColor }) => {
  const prevValueRef = useRef(value);
  const directionRef = useRef('neutral');
  //--> V12B-07: resolve ONCE per price, on the wrapper, not once per digit. A price
  //*   renders ~8 TickerDigits and getComputedStyle forces a style recalc, so doing
  //*   it per digit would put 8 recalcs on a per-tick path.
  const wrapperRef = useRef(null);
  const resolvedBaseColor = useInheritedColor(wrapperRef, baseColor);

  // Update direction based on value change
  if (value !== prevValueRef.current) {
    if (value > prevValueRef.current) directionRef.current = 'up';
    else if (value < prevValueRef.current) directionRef.current = 'down';
    prevValueRef.current = value;
  }

  // Determine signal color for the flash
  // If direction is neutral, start with the base color (if available) to avoid currentColor mismatch
  //! V12B-07: the neutral case fell back to the `currentColor` keyword too. Same
  //!  fix — use the resolved colour; null on the first frame is handled by omitting
  //!  the colour key entirely rather than feeding framer a keyword it cannot animate.
  //!  The up/down flash colours are UNCHANGED.
  const signalColor = directionRef.current === 'up'
    ? '#00FF00'
    : (directionRef.current === 'down' ? '#FF0000' : resolvedBaseColor);

  // Format value
  const formatted = typeof value === 'number' 
    ? new Intl.NumberFormat('en-IN', { 
        style: 'decimal', 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(value)
    : String(value || 0);

  return (
    //--> `items-center leading-none` gives all children one vertical reference
    //*   (the flex cross-axis center) and strips line-height padding that
    //*   would otherwise push text baselines out of sync with the digits'
    //*   flex-centered 1em boxes. Everything now sits on the same horizontal
    //*   line visually, regardless of glyph type.
    <div ref={wrapperRef} className={`flex items-center leading-none ${className}`}>
      {/* --> Currency prefix in a 1em-tall flex-centered box, same idiom as
               the digit and non-digit containers, with a 2s color transition
               matching the motion.span timing. This keeps ₹ on the same
               baseline and fading in sync with the digits. */}
      <span
        className="inline-flex items-center justify-center h-[1em] text-[0.75em] opacity-70 mr-0.5"
        style={{ transition: NON_DIGIT_COLOR_TRANSITION }}
      >
        ₹
      </span>
      {formatted.split('').map((char, index) => (
        <TickerDigit
            key={index} // persistence is key for AnimatePresence inside child
            char={char}
            direction={directionRef.current}
            signalColor={signalColor}
            baseColor={resolvedBaseColor}
        />
      ))}
    </div>
  );
};

export default TickerPrice;
